import { AttendanceGuage, Button } from '@wot/components';
import { IStudent, ITransaction, ITrialClass } from '@wot/typings';
import { compareAsc, parseJSON } from 'date-fns';
import { format } from 'date-fns/esm';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { Link, Outlet, useNavigate, useParams } from 'react-router-dom';
import studentsApi from '../../../../api/students';
import { DEFAULT_PROFILE_PIC } from '../../../../assets/icons';
import { Table, TableBody, TableHeading, TableHeadingCol, TableRow } from '../../../../components/Table';
import { StudentPlan } from '../../../../interfaces/Plan';
import xhr from '../../../../utils/fetch';
import { getAttendacePercentage } from '../../../../utils/functions';
import { TableRowItem } from '../components/TableRowItem';
import LoadingContent from '../../../../components/LoadingContent';
import { IoMdArrowBack } from 'react-icons/io';
import BreadCrumb from '../../../../components/BreadCrumb';

export default function StudentDashboard() {
  const { student_id } = useParams();
  const [student, setStudent] = useState<IStudent>();
  const [plans, setPlans] = useState<any[]>([]);
  const [activePlans, setActivePlans] = useState<StudentPlan[]>([]);
  const [currentPath, setCurrentPath] = useState('overview');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (student) {
      let paths = location.pathname.split('/');
      if (paths[paths.length - 1] === student?._id) {
        setCurrentPath('overview');
      } else {
        setCurrentPath(paths[paths.length - 1]);
      }
    }
  }, [location.pathname, student]);

  useEffect(() => {
    if (!student_id) {
      return;
    }
    setLoading(true);
    studentsApi
      .getStudentProfile(student_id)
      .then((data) => {
        console.log(data);
        setLoading(false);
        setStudent(data);
      })
      .catch(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    xhr({
      url: `/u/${student_id}/plans`,
      method: 'GET'
    }).then((res) => {
      if (res.status === 'OK') {
        setPlans(res.data);
      }
    });
  }, []);

  useEffect(() => {
    if (plans) {
      let temp = JSON.parse(JSON.stringify(plans));
      let sortedPlans = temp.sort((a: any, b: any) => compareAsc(new Date(a.createdAt), new Date(b.createdAt)));
      let active = sortedPlans.filter((item: any) => item.status === 'active');
      setActivePlans(active);
    }
  }, [plans]);

  if (loading) {
    return <LoadingContent />;
  }

  if (!student) {
    return <div className="w-full h-full flex flex-col items-center justify-center">No Profile Found.</div>;
  }

  return (
    <div className="w-full flex flex-col gap-4 p-4 grow">
      <BreadCrumb />
      <div className='flex items-center my-2 gap-2'>
        <IoMdArrowBack className='text-xl cursor-pointer' onClick={() => navigate(-1)} />
        <h2 className="text-xl font-bold text-black">
          {`${student.name}'s `}
          <span className="capitalize">{currentPath}</span>
        </h2>
      </div>
      {currentPath === 'overview' ? (
        <>
          <div className="w-full h-full flex flex-col gap-5 py-2 px-2">
            <table className="w-full h-full table-auto">
              <TableHeading>
                <TableRow>
                  <TableHeadingCol label="DATE" />
                  <TableHeadingCol label="STUDENT ID" />
                  <TableHeadingCol label="NAME" />
                  <TableHeadingCol label="EMAIL" />
                  <TableHeadingCol label="PHONE NO." />
                  <TableHeadingCol label="STATUS" />
                </TableRow>
              </TableHeading>
              <TableBody>
                <TableRow>
                  <TableRowItem student={student} />
                </TableRow>
              </TableBody>
            </table>
          </div>
          <div className="grid grid-cols-3 grid-rows-2 grid-flow-row gap-4">
            <div className="flex flex-col row-span-1 col-span-1 h-full items-center bg-white">
              <Profile
                student={student}
                activePlans={activePlans.length}
                tutors={_.uniqBy(plans, (plan) => plan.tutor && plan.tutor.name).length}
                subjects={_.uniqBy(plans, (plan) => plan.category.name).length}
              />
            </div>
            <div className="bg-white row-span-2 col-span-1 rounded-lg">
              <UpcomingSessions />
            </div>
            <div className="bg-white row-span-1 col-span-1 rounded-lg">
              <ActivePlans activePlans={activePlans} />
            </div>
            <div className="row-span-1 col-span-1 bg-white rounded-lg">
              <Attendance />
            </div>
            <div className="w-full row-span-1 col-span-1 bg-white rounded-lg">
              <Transactions />
            </div>
          </div>
        </>
      ) : (
        <Outlet />
      )}
    </div>
  );
}

interface ProfileProps {
  student: IStudent;
  activePlans: number;
  tutors: number;
  subjects: number;
}
const Profile = ({ student, activePlans, subjects, tutors }: ProfileProps) => {
  return (
    <div className="h-full rounded-2xl border border-neutralGray3 p-5 w-full flex flex-col items-center gap-3">
      <div className="flex flex-col items-center gap-3">
        <div className="h-20 w-20 rounded-full overflow-hidden">
          <img
            src={student?.profilePic === '' ? DEFAULT_PROFILE_PIC : student?.profilePic}
            alt="User Profile Pic"
            className="w-full h-full object-cover"
          />
        </div>
        <div>
          <span className="text-xl font-normal">{student.name}</span>
        </div>
      </div>
      <div className="flex justify-center divide-x gap-4">
        <div className="flex flex-col items-center px-1">
          <span className="text-sm font-medium">{activePlans}</span>
          <span className="text-xs font-medium text-neutralGray2">Active Plans</span>
        </div>
        <div className="flex flex-col items-center px-1">
          <span className="text-sm font-medium">{tutors}</span>
          <span className="text-xs font-medium text-neutralGray2">Tutors</span>
        </div>
        <div className="flex flex-col items-center px-1">
          <p className="text-sm font-medium">{subjects}</p>
          <span className="text-xs font-medium text-neutralGray2">Subjects</span>
        </div>
      </div>
      <Link to={'profile'}>
        <div className="w-full max-w-[150px] mx-auto">
          <Button type="secondary" text="View Profile" size="md" onClick={() => { }} />
        </div>
      </Link>
    </div>
  );
};

const Attendance = () => {
  const [totalSessions, setTotalSessions] = useState<ITrialClass[]>([]);
  const { student_id } = useParams();

  useEffect(() => {
    xhr({
      url: `/u/${student_id}/sessions`,
      method: 'GET',
      params: {
        type: 'plan',
        status: 'completed'
      }
    })
      .then((res) => {
        if (res.status === 'OK') {
          setTotalSessions(res.data);
        }
      })
      .catch(() => { });
  }, []);
  return (
    <div className="rounded-2xl border border-neutralGray3 p-5 w-full h-full flex flex-col items-center gap-3">
      <div className="w-full flex flex-row justify-between items-center">
        <span className="text-base font-medium">Attendance</span>
        <Link to={'attendance'}>
          <span className="text-sm font-normal text-black/80 cursor-pointer hover:text-primary hover:underline hover:underline-offset-1">
            View details
          </span>
        </Link>
      </div>
      <div className="w-[40%] md:w-[50%] text-2xl grow flex items-center justify-center p-4">
        <AttendanceGuage
          percentage={getAttendacePercentage(
            totalSessions.filter((i) => i.attendance && i.attendance.student === 'present').length,
            totalSessions.length
          )}
        />
      </div>
    </div>
  );
};

const ActivePlans = ({ activePlans }: { activePlans: StudentPlan[] }) => {
  return (
    <div className="rounded-2xl border border-neutralGray3 p-5 w-full h-full flex flex-col items-center gap-3">
      <div className="w-full flex flex-row justify-between items-center">
        <span className="text-base font-medium">Active Plans ({activePlans.length})</span>
        <Link to={'plans'}>
          <span className="text-sm font-normal text-black/80 cursor-pointer hover:text-primary hover:underline hover:underline-offset-1">
            View details
          </span>
        </Link>
      </div>
      {activePlans.slice(0, 2).map((plan) => (
        <div key={plan._id} className="w-full border border-primary rounded-lg">
          <div className="border-l-4 border-primary rounded-lg">
            <div className="px-6 py-4 border-b-2 border-neutralBlue1">
              <span className="flex flex-col gap-1">
                <p className="text-xl font-medium">{plan.tutor.name}</p>
                <p className="text-base text-neutralGray2 font-normal"> {plan.totalSessions} Classes / 1 hour</p>
              </span>
            </div>
            <div className="px-6 py-4 flex flex-col gap-2">
              <span className="flex justify-between text-base text-neutralGray2 font-normal">
                <p>Scheduled Lessons</p>
                <p>
                  {plan.sessions?.length || 0}/{plan.totalSessions}
                </p>
              </span>
              <div className="relative w-full h-2 bg-neutralBlue1 rounded-full">
                <div
                  style={{
                    width: `${plan.sessions ? (plan.sessions?.length / plan.totalSessions) * 100 : 0}%`
                  }}
                  className="absolute bg-primary w-full h-full top-0 left-0 rounded-full"
                ></div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const UpcomingSessions = () => {
  const { student_id } = useParams();
  const [upcomingSessions, setUpcomingSessions] = useState<ITrialClass[]>([]);

  useEffect(() => {
    xhr({
      url: `/u/${student_id}/sessions`,
      method: 'GET'
    }).then((res) => {
      if (res.status === 'OK') {
        let sortedSlots = res.data.sort((a: any, b: any) =>
          compareAsc(new Date(a.scheduledTime.start), new Date(b.scheduledTime.start))
        );
        let sessions = sortedSlots.filter((item: any) => item.status === 'upcoming');
        setUpcomingSessions(sessions);
      }
    });
  }, []);

  return (
    <div className="h-full rounded-2xl border border-neutralGray3 p-5 w-full flex flex-col gap-4">
      <div className="w-full flex flex-row justify-between items-center">
        <span className="text-base font-medium">Upcoming Sessions</span>
        <Link to={'calendar'}>
          <span className="text-sm font-normal text-black/80 cursor-pointer hover:text-primary hover:underline hover:underline-offset-1">
            View details
          </span>
        </Link>
      </div>
      <div className="flex flex-col gap-7">
        {upcomingSessions.slice(0, 4).map((session) => (
          <div key={session._id} className="flex gap-4">
            <div className="w-16 h-16 overflow-hidden rounded-full">
              <img src={session.tutor.profilePic || DEFAULT_PROFILE_PIC} className="w-full h-full object-cover" />
            </div>
            <div className="flex flex-col gap-2">
              <h2 className="text-xl font-semibold">{session.tutor.name}</h2>
              <h2 className="text-base font-medium">{session.category.name} Tution</h2>
              <p className="text-sm font-medium">
                {format(parseJSON(session.scheduledTime.start), 'EEEE, LLL d,')}{' '}
                {format(parseJSON(session.scheduledTime.start), 'p')} -{' '}
                {format(parseJSON(session.scheduledTime.end), 'p')}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const Transactions = () => {
  const { student_id } = useParams();
  const [transactions, setTransactions] = useState<ITransaction[]>([]);

  useEffect(() => {
    if (!student_id) {
      return;
    }
    xhr({
      url: `/u/transactions`,
      method: 'GET',
      params: {
        userId: student_id
      }
    }).then((res) => {
      if (res.status === 'OK') {
        setTransactions(res.data);
      }
    });
  }, []);

  return (
    <div className="h-full rounded-2xl border border-neutralGray3 p-5 w-full flex flex-col items-center gap-3">
      <div className="w-full flex flex-row justify-between items-center">
        <span className="text-base font-medium">Transactions</span>
        <Link to={'transactions'}>
          <span className="text-sm font-normal text-black/80 cursor-pointer hover:text-primary hover:underline hover:underline-offset-1">
            View details
          </span>
        </Link>
      </div>
      <div className="w-full text-sm overflow-hidden">
        <table className="w-full">
          <thead>
            <th>Date</th>
            <th>Trans. ID</th>
            <th>Amount</th>
          </thead>
          <tbody>
            {transactions.slice(0, 5).map((transaction) => (
              <tr key={transaction._id}>
                <td className="py-2">{format(parseJSON(transaction.createdAt), 'd/M/y')}</td>
                <td className="py-2 max-w-[200px] overflow-ellipsis overflow-hidden">{transaction._id}</td>
                <td className="py-2 uppercase whitespace-nowrap">
                  {transaction.currency} {transaction.amount}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
